import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Package,
  Wrench,
  CheckCircle,
  AlertTriangle,
  Clock,
  Plus,
  FileSpreadsheet,
  Printer,
  Users,
  ArrowRight,
  Box
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { BulkImportDialog } from "@/components/BulkImportDialog";
import { MechanicWorksheet } from "@/components/MechanicWorksheet";

export default function ReceptionPage() {
  const [stats, setStats] = useState(null);
  const [scooters, setScooters] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [showWorksheet, setShowWorksheet] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, scootersRes, mechanicsRes, overdueRes] = await Promise.all([
        api.get("/reception/stats"),
        api.get("/reception/scooters"),
        api.get("/mechanics/summary"),
        api.get("/reception/overdue?days=7")
      ]);
      setStats(statsRes.data);
      setScooters(scootersRes.data);
      setMechanics(mechanicsRes.data.mechanics || []);
      setOverdue(overdueRes.data.scooters || []);
    } catch (error) {
      toast.error("Erreur lors du chargement des donnees");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case 1:
        return <Badge className="bg-red-500 text-white">Urgent</Badge>;
      case 2:
        return <Badge className="bg-yellow-500 text-black">Normal</Badge>;
      case 3:
        return <Badge className="bg-blue-500 text-white">Peut attendre</Badge>;
      default:
        return <Badge variant="outline">-</Badge>;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "reception":
        return <Badge className="bg-purple-500 text-white">Reception</Badge>;
      case "preparation":
        return <Badge className="bg-orange-500 text-white">Preparation</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getProgressPercent = (tasks) => {
    if (!tasks || tasks.length === 0) return 0;
    const completed = tasks.filter(t => t.status === "completed").length;
    return Math.round((completed / tasks.length) * 100);
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <div className="animate-pulse text-primary font-mono">Chargement...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="reception-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading font-black text-2xl md:text-3xl flex items-center gap-3">
              <Package className="w-8 h-8 text-primary" />
              Reception & Preparation
            </h1>
            <p className="text-muted-foreground mt-1">
              Gestion des scooters de la reception a la vente
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setShowWorksheet(true)}
              data-testid="btn-print-worksheet"
            >
              <Printer className="w-4 h-4 mr-2" />
              Fiche Mecanicien
            </Button>
            <Button
              onClick={() => setShowImportDialog(true)}
              className="bg-primary text-primary-foreground"
              data-testid="btn-bulk-import"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Import en Masse
            </Button>
            <Link to="/add-scooter">
              <Button className="bg-green-600 hover:bg-green-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Nouveau Scooter
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-purple-500/30 bg-purple-500/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Box className="w-8 h-8 text-purple-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.reception_count || 0}</p>
                  <p className="text-sm text-muted-foreground">En Reception</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-orange-500/30 bg-orange-500/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Wrench className="w-8 h-8 text-orange-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.preparation_count || 0}</p>
                  <p className="text-sm text-muted-foreground">En Preparation</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-red-500/30 bg-red-500/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-8 h-8 text-red-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.urgent_count || 0}</p>
                  <p className="text-sm text-muted-foreground">Urgents</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-yellow-500/30 bg-yellow-500/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Clock className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold">{stats?.overdue_count || 0}</p>
                  <p className="text-sm text-muted-foreground">En retard (+7j)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Overdue Alert */}
        {overdue.length > 0 && (
          <Card className="border-red-500 bg-red-500/10">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-500 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Scooters en attente depuis plus de 7 jours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {overdue.slice(0, 5).map((s) => (
                  <Link key={s.scooter_id} to={`/stock/${s.scooter_id}`}>
                    <Badge variant="outline" className="cursor-pointer hover:bg-red-500/20">
                      {s.scooter_code || s.scooter_id} - {s.brand} {s.model} ({s.days_waiting}j)
                    </Badge>
                  </Link>
                ))}
                {overdue.length > 5 && (
                  <Badge variant="outline">+{overdue.length - 5} autres</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue="scooters" className="w-full">
          <TabsList className="w-full justify-start">
            <TabsTrigger value="scooters" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              Scooters ({scooters.length})
            </TabsTrigger>
            <TabsTrigger value="mechanics" className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              Mecaniciens
            </TabsTrigger>
          </TabsList>

          <TabsContent value="scooters" className="mt-4">
            <div className="space-y-4">
              {scooters.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center">
                    <Package className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucun scooter en reception ou preparation</p>
                    <Link to="/add-scooter">
                      <Button className="mt-4">
                        <Plus className="w-4 h-4 mr-2" />
                        Ajouter un scooter
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ) : (
                scooters.map((scooter) => {
                  const receptionData = scooter.reception_data || {};
                  const tasks = receptionData.repair_tasks || [];
                  const progress = getProgressPercent(tasks);
                  const completedTasks = tasks.filter(t => t.status === "completed").length;
                  
                  return (
                    <Card key={scooter.scooter_id} className="hover:border-primary/50 transition-colors">
                      <CardContent className="p-4">
                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                          {/* Scooter Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {getPriorityBadge(receptionData.repair_priority)}
                              {getStatusBadge(scooter.status)}
                              {receptionData.scooter_code && (
                                <Badge variant="outline" className="font-mono">
                                  {receptionData.scooter_code}
                                </Badge>
                              )}
                            </div>
                            <Link to={`/stock/${scooter.scooter_id}`}>
                              <h3 className="font-bold text-lg hover:text-primary transition-colors">
                                {scooter.brand} {scooter.model} {scooter.year}
                              </h3>
                            </Link>
                            <p className="text-sm text-muted-foreground font-mono">
                              {scooter.scooter_id} • VIN: {scooter.vin || "-"}
                            </p>
                          </div>

                          {/* Progress */}
                          <div className="w-full md:w-48">
                            <div className="flex justify-between text-sm mb-1">
                              <span>Taches</span>
                              <span className="font-mono">{completedTasks}/{tasks.length}</span>
                            </div>
                            <Progress value={progress} className="h-2" />
                            <p className="text-xs text-muted-foreground mt-1 text-right">
                              {progress}% termine
                            </p>
                          </div>

                          {/* Action */}
                          <Link to={`/stock/${scooter.scooter_id}`}>
                            <Button variant="outline" size="sm">
                              Voir <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>

                        {/* Tasks Preview */}
                        {tasks.length > 0 && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex flex-wrap gap-2">
                              {tasks.slice(0, 4).map((task) => (
                                <Badge
                                  key={task.task_id}
                                  variant={task.status === "completed" ? "default" : "outline"}
                                  className={
                                    task.status === "completed"
                                      ? "bg-green-500 text-white"
                                      : task.status === "in_progress"
                                      ? "border-orange-500 text-orange-500"
                                      : ""
                                  }
                                >
                                  {task.status === "completed" && <CheckCircle className="w-3 h-3 mr-1" />}
                                  {task.task_name}
                                </Badge>
                              ))}
                              {tasks.length > 4 && (
                                <Badge variant="outline">+{tasks.length - 4}</Badge>
                              )}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="mechanics" className="mt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {mechanics.length === 0 ? (
                <Card className="col-span-full border-dashed">
                  <CardContent className="p-8 text-center">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">Aucune tache assignee</p>
                  </CardContent>
                </Card>
              ) : (
                mechanics.map((mechanic) => (
                  <Card key={mechanic.name} className="hover:border-primary/50 transition-colors">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Wrench className="w-5 h-5 text-primary" />
                        {mechanic.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">En attente</span>
                          <Badge variant="outline">{mechanic.pending || 0}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">En cours</span>
                          <Badge className="bg-orange-500 text-white">{mechanic.in_progress || 0}</Badge>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-muted-foreground">Terminees</span>
                          <Badge className="bg-green-500 text-white">{mechanic.completed || 0}</Badge>
                        </div>
                        <div className="pt-2 border-t">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">Cout estime</span>
                            <span className="font-mono">{mechanic.total_estimated_cost || 0} EUR</span>
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-4"
                        onClick={() => {
                          setSelectedMechanic(mechanic.name);
                          setShowWorksheet(true);
                        }}
                      >
                        <Printer className="w-4 h-4 mr-2" />
                        Imprimer Fiche
                      </Button>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dialogs */}
      <BulkImportDialog
        open={showImportDialog}
        onOpenChange={setShowImportDialog}
        onSuccess={() => {
          setShowImportDialog(false);
          fetchData();
        }}
      />

      <MechanicWorksheet
        open={showWorksheet}
        onOpenChange={setShowWorksheet}
        mechanic={selectedMechanic}
        onClose={() => {
          setShowWorksheet(false);
          setSelectedMechanic(null);
        }}
      />
    </Layout>
  );
}
